import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage
} from '@epoch-ui/components/Form/form';
import { Badge } from '@epoch-ui/components/Badge';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@epoch-ui/components/DropdownMenu/dropdown-menu';
import { Icon } from '@epoch-ui/components/Icon';

interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectFieldProps {
  name: string;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  placeholder?: string;
  options: MultiSelectOption[];
  disabled?: boolean;
  className?: string;
}

const MultiSelectField = React.forwardRef<HTMLButtonElement, MultiSelectFieldProps>(
  (
    { name, label, description, placeholder = 'Select options...', options, disabled, className },
    ref
  ) => {
    const formCtx = useFormContext();

    return (
      <FormField
        control={formCtx.control}
        name={name}
        render={({ field, fieldState }) => {
          const selectedValues = field.value || [];

          const handleSelect = (optionValue: string) => {
            const newValues = selectedValues.includes(optionValue)
              ? selectedValues.filter((value: string) => value !== optionValue)
              : [...selectedValues, optionValue];
            field.onChange(newValues);
          };

          const handleRemove = (optionValue: string) => {
            const newValues = selectedValues.filter((value: string) => value !== optionValue);
            field.onChange(newValues);
          };

          const getSelectedLabels = () => {
            return selectedValues.map((value: string) => {
              const option = options.find((opt) => opt.value === value);
              return option ? option.label : value;
            });
          };

          return (
            <FormItem className={cn('tw-mt-4', className)}>
              {label && (
                <FormLabel className={cn('tw-mb-0', disabled && 'tw-opacity-40')}>
                  {label}
                </FormLabel>
              )}
              {description && (
                <FormDescription className={cn('tw-m-0', disabled && 'tw-opacity-40')}>
                  {description}
                </FormDescription>
              )}
              <FormControl>
                <div className="tw-relative">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        ref={ref}
                        type="button"
                        disabled={disabled}
                        className={cn(
                          'tw-flex tw-h-auto tw-min-h-[48px] tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border-[1.5px] tw-border-solid tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus-visible:tw-border-primary-blue disabled:tw-cursor-not-allowed disabled:tw-opacity-50',
                          fieldState.error && 'tw-border-destructive-foreground',
                          'tw-mt-0'
                        )}
                      >
                        <div className="tw-flex tw-flex-1 tw-flex-wrap tw-gap-1">
                          {selectedValues.length > 0 ? (
                            getSelectedLabels().map((optionLabel: string, index: number) => (
                              <Badge
                                key={selectedValues[index]}
                                variant="secondary"
                                className="tw-flex tw-items-center tw-gap-1 tw-py-1 tw-pl-2 tw-text-xs"
                              >
                                {optionLabel}
                                <span
                                  role="button"
                                  aria-label={`Remove ${optionLabel}`}
                                  tabIndex={0}
                                  onPointerDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleRemove(selectedValues[index]);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleRemove(selectedValues[index]);
                                    }
                                  }}
                                  className="tw-cursor-pointer tw-rounded-full tw-p-0 tw-outline-none hover:tw-bg-secondary-foreground/20 focus:tw-bg-secondary-foreground/20"
                                >
                                  <Icon name="x" size={12} />
                                </span>
                              </Badge>
                            ))
                          ) : (
                            <span className="tw-text-muted-foreground">{placeholder}</span>
                          )}
                        </div>
                        <Icon name="chevron-down" size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="tw-w-full tw-min-w-[var(--radix-dropdown-menu-trigger-width)]">
                      {options.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={(e) => {
                            e.preventDefault();
                            handleSelect(option.value);
                          }}
                          className={cn(
                            'tw-cursor-pointer tw-px-3 tw-py-2',
                            selectedValues.includes(option.value) && 'tw-bg-accent'
                          )}
                        >
                          <div className="tw-flex tw-items-center tw-gap-3">
                            <Checkbox
                              checked={selectedValues.includes(option.value)}
                              className="tw-mt-0"
                            />
                            {option.label}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  }
);

MultiSelectField.displayName = 'MultiSelectField';

export { MultiSelectField };
export type { MultiSelectOption, MultiSelectFieldProps };
